import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Info } from 'lucide-react';
import React from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Button, formatNumber } from '@column/column-ui-kit';
import { Alert, AlertDescription, AlertTitle, Badge, Timestamp } from '@column/ui';

import { Section, TimelineEvents } from '~/components';
import { invalidateCardDisputeQueries, useCanManageCardDisputes } from '~/lib/api/cardDisputes';
import {
  CARD_DISPUTE_NEXT_ACTION_COPY,
  DISPUTE_SYNC_NOTE,
  formatRespondBy,
  getDisputeReasonLabel,
  getDisputeResolutionLabel,
  getDisputeStageBadgeClasses,
  getDisputeStageLabel,
  getDisputeStatusBadgeClasses,
  getDisputeStatusLabel,
  getSubmissionStatusBadgeClasses,
  getSubmissionStatusLabel,
  getWorkflowLabel,
} from '~/lib/disputes/labels';
import { buildCardDisputeTimelineEvents } from '~/lib/disputes/timeline';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { CardDisputeProps } from './CardDispute';
import { DisputeDraftRepairPanel } from './Initiation/DisputeDraftRepairPanel';
import { DisputeActions, DisputeArbitrationTrackingSlot, DisputeLifecycleGraphSlot } from './slots';

const FAILED_SUBMISSION_FOOTER_COPY = 'Column will not retry automatically — contact support or adjust the dispute.';

const SummaryItem = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-border bg-card px-4 py-3">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="truncate text-sm font-medium text-foreground">{children}</span>
  </div>
);

export const PageCardDisputeInformation: React.FC = () => {
  const { cardDispute: outletCardDispute } = useOutletContext<CardDisputeProps>();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const canManage = useCanManageCardDisputes();

  if (!outletCardDispute) {
    return null;
  }

  const cardDispute = outletCardDispute;
  const nextAction = cardDispute.next_actions?.[0];
  const nextActionCopy = nextAction ? CARD_DISPUTE_NEXT_ACTION_COPY[nextAction] : undefined;
  const timelineEvents = buildCardDisputeTimelineEvents(cardDispute);
  const isTerminal = cardDispute.stage === 'dispute_won' || cardDispute.stage === 'dispute_close';

  // The stored flag records that credit was issued; a later reversal does not clear it, so it is
  // presented as issuance history rather than a current outstanding credit balance.
  const provisionalCreditCopy = cardDispute.provisional_credit
    ? 'Issued — later reversals are not reflected here'
    : 'Not issued';

  const onUpdated = () => invalidateCardDisputeQueries(queryClient, { disputeId: id });

  return (
    <Inner p={0}>
      <Inner px="var(--page-margin)" py={16}>
        <div className="flex flex-col gap-4">
          <DisputeLifecycleGraphSlot dispute={cardDispute} canManage={canManage} actionsHref="#dispute-actions" />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryItem label="Amount">{formatNumber(cardDispute.amount, cardDispute.currency)}</SummaryItem>
            <SummaryItem label="Reason">{getDisputeReasonLabel(cardDispute.reason)}</SummaryItem>
            <SummaryItem label="Workflow">{getWorkflowLabel(cardDispute.workflow)}</SummaryItem>
            <SummaryItem label="Stage">
              <Badge variant="outline" className={getDisputeStageBadgeClasses(cardDispute.stage)}>
                {getDisputeStageLabel(cardDispute.stage)}
              </Badge>
            </SummaryItem>
            <SummaryItem label="Status">
              <Badge variant="outline" className={getDisputeStatusBadgeClasses(cardDispute.status)}>
                {getDisputeStatusLabel(cardDispute.status)}
              </Badge>
            </SummaryItem>
            <SummaryItem label="Submission Status">
              <Badge variant="outline" className={getSubmissionStatusBadgeClasses(cardDispute.submission_status)}>
                {getSubmissionStatusLabel(cardDispute.submission_status)}
              </Badge>
            </SummaryItem>
            <SummaryItem label="Respond By">
              {cardDispute.respond_by ? (
                <span className="whitespace-nowrap">{formatRespondBy(cardDispute.respond_by)}</span>
              ) : (
                '—'
              )}
            </SummaryItem>
            <SummaryItem label="Resolution">
              {isTerminal ? getDisputeResolutionLabel(cardDispute.resolution_reason, cardDispute.stage) : '—'}
            </SummaryItem>
            <SummaryItem label="Provisional Credit">{provisionalCreditCopy}</SummaryItem>
            <SummaryItem label="Last Network Sync">
              {cardDispute.last_network_sync_at ? (
                <Timestamp
                  date={cardDispute.last_network_sync_at}
                  formatHover={(hoverDate) => formatDateTime(hoverDate, { timeZoneFormat: 'utc' })}
                >
                  {formatDateTime(cardDispute.last_network_sync_at, { timeZoneFormat: 'local' })}
                </Timestamp>
              ) : (
                '—'
              )}
            </SummaryItem>
          </div>

          {cardDispute.submission_status === 'failed' && (
            <Alert variant="destructive">
              <CircleAlert className="size-4" />
              <AlertTitle>Submission failed</AlertTitle>
              <AlertDescription className="gap-2">
                <p className="break-words font-mono text-xs">{cardDispute.submission_error}</p>
                <p>{FAILED_SUBMISSION_FOOTER_COPY}</p>
              </AlertDescription>
            </Alert>
          )}

          {cardDispute.submission_status === 'requires_action' && (
            <Alert>
              <Info className="size-4 text-blue-600 dark:text-blue-400" />
              <AlertTitle>Response needed</AlertTitle>
              <AlertDescription>
                <p>The network is waiting on your response. Respond below to keep this dispute moving.</p>
              </AlertDescription>
            </Alert>
          )}

          {nextActionCopy && (
            <Alert>
              <Info className="size-4" />
              <AlertTitle>{nextActionCopy.label}</AlertTitle>
              <AlertDescription className="gap-2">
                <p>{nextActionCopy.description}</p>
                {nextActionCopy.ctaLabel && canManage && (
                  <Button variant="secondary" size="small" className="pointer-events-none mt-1" aria-disabled>
                    {nextActionCopy.ctaLabel}
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-muted-foreground">{DISPUTE_SYNC_NOTE}</p>

          {cardDispute.submission_status === 'draft' && (
            <DisputeDraftRepairPanel dispute={cardDispute} onUpdated={onUpdated} />
          )}

          <DisputeArbitrationTrackingSlot dispute={cardDispute} canManage={canManage} onUpdated={onUpdated} />
          <DisputeActions dispute={cardDispute} canManage={canManage} onUpdated={onUpdated} />
        </div>
      </Inner>

      <div id="dispute-history" data-dd-privacy="hidden">
        <Section headline="History">
          <TimelineEvents entries={timelineEvents} title="Timeline" />
        </Section>
      </div>
    </Inner>
  );
};
